import { IconArrowUpRight, IconSparkles } from "@tabler/icons-react"
import { type FC } from "react"

import { type BlogPost as BlogPostType } from "@forento/core/models/dashboard"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import { toRelativeDateString } from "~/utilities/date"

const productUpdatesLink = "https://forento.link/dash-as-productupdates"
const creatorHighlightsLink = "https://forento.link/dash-as-creatorhighlights"

type Props = { productUpdates: BlogPostType[]; creatorHighlights: BlogPostType[] }
const LatestPosts: FC<Props> = ({ productUpdates, creatorHighlights }) => {
	const showCreatorHighlights = creatorHighlights.length > 0
	if (productUpdates.length === 0 && !showCreatorHighlights) return null

	const latestProductUpdate = productUpdates[0]
	const secondaryProductUpdates = productUpdates.slice(1)
	const primaryLink = productUpdates.length > 0 ? productUpdatesLink : creatorHighlightsLink
	const primaryLinkLabel = productUpdates.length > 0 ? "See all updates" : "See all highlights"

	return (
		<section className="flex flex-col gap-4 rounded-3xl bg-[#f9f5ff] p-3 sm:p-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="text-primary text-sm font-semibold">Forento news</p>
					<h2 className="text-xl font-semibold text-black">What's new</h2>
					{productUpdates.length > 0 && (
						<p className="mt-1 text-sm leading-5 text-gray-600">
							We ship updates every day. These are some of the most requested and notable improvements.
						</p>
					)}
				</div>
				<Button
					className="text-primary shrink-0 items-center gap-1 text-sm font-semibold"
					newTab
					onClick={primaryLink}
				>
					{primaryLinkLabel}
					<IconArrowUpRight className="size-4" />
				</Button>
			</div>
			<div className="flex flex-wrap items-start gap-4">
				{productUpdates.length > 0 && (
					<div
						className={cn(
							"flex min-w-0 flex-col gap-3",
							creatorHighlights.length > 0 ? "min-w-[min(100%,728px)] flex-[999_1_728px]" : "w-full",
						)}
					>
						{latestProductUpdate && (
							<a
								href={latestProductUpdate.link}
								target="_blank"
								rel="noreferrer noopener"
								className="group hover:text-primary flex cursor-pointer flex-wrap overflow-hidden rounded-2xl bg-white text-black transition-colors"
							>
								{renderPostThumbnail(
									latestProductUpdate,
									"aspect-video min-w-[min(100%,220px)] flex-[1_1_220px]",
									"size-8",
								)}
								<div className="flex min-w-[min(100%,280px)] flex-[999_1_280px] flex-col justify-between gap-4 p-4">
									<div className="min-w-0">
										<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
											<p className="text-primary text-xs font-semibold">Latest product update</p>
											<p className="text-xs font-medium text-gray-500">
												{toRelativeDateString(latestProductUpdate.date)}
											</p>
										</div>
										<h3 className="mt-2 line-clamp-3 text-xl leading-tight font-semibold">
											{latestProductUpdate.title}
										</h3>
									</div>
									<span className="text-primary flex items-center gap-1 text-sm font-semibold">
										Read update
										<IconArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
									</span>
								</div>
							</a>
						)}
						{secondaryProductUpdates.length > 0 && (
							<div className="grid grid-cols-2 gap-2 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
								{secondaryProductUpdates.map(post => (
									<a
										key={post.link}
										href={post.link}
										target="_blank"
										rel="noreferrer noopener"
										className="group hover:text-primary flex cursor-pointer flex-col gap-3 rounded-2xl bg-white/70 p-3 text-black transition-colors hover:bg-white"
									>
										{renderPostThumbnail(post, "aspect-video w-full rounded-xl", "size-6")}
										<div className="flex flex-1 items-start justify-between gap-2">
											<div className="min-w-0">
												<p className="line-clamp-2 text-sm leading-5 font-semibold">
													{post.title}
												</p>
												<p className="mt-1 text-xs font-medium text-gray-500">
													{toRelativeDateString(post.date)}
												</p>
											</div>
											<IconArrowUpRight className="text-primary size-4 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
										</div>
									</a>
								))}
							</div>
						)}
					</div>
				)}
				{showCreatorHighlights && (
					<div
						className={cn(
							"flex flex-col gap-3 rounded-2xl bg-white/50 p-3",
							productUpdates.length > 0 ? "min-w-[min(100%,280px)] flex-[1_1_280px]" : "w-full",
						)}
					>
						<div className="flex flex-wrap items-center justify-between gap-3">
							<h3 className="text-base font-semibold text-black">Creator highlights</h3>
							<Button
								className="text-primary shrink-0 items-center gap-1 text-sm font-semibold"
								newTab
								onClick={creatorHighlightsLink}
							>
								View all
								<IconArrowUpRight className="size-4" />
							</Button>
						</div>
						<div className="flex flex-wrap gap-2">
							{creatorHighlights.map(post => (
								<a
									key={post.link}
									href={post.link}
									target="_blank"
									rel="noreferrer noopener"
									className="group hover:text-primary flex min-w-[min(100%,220px)] flex-1 cursor-pointer gap-3 rounded-2xl bg-white/70 p-3 text-black transition-colors hover:bg-white"
								>
									{renderPostThumbnail(post, "size-12 shrink-0 rounded-xl", "size-5")}
									<div className="min-w-0 flex-1">
										<p className="line-clamp-2 text-sm leading-5 font-semibold">{post.title}</p>
										<p className="mt-1 text-xs font-medium text-gray-500">
											{toRelativeDateString(post.date)}
										</p>
									</div>
								</a>
							))}
						</div>
					</div>
				)}
			</div>
		</section>
	)
}

function renderPostThumbnail(post: BlogPostType, className: string, iconClassName: string) {
	if (post.thumbnailFilePath) {
		return (
			<div className={cn(className, "bg-primary/10 overflow-hidden")}>
				<img src={post.thumbnailFilePath} alt="" className="size-full object-cover" />
			</div>
		)
	}

	return (
		<div className={cn(className, "bg-primary/10 text-primary flex items-center justify-center")}>
			<IconSparkles className={iconClassName} />
		</div>
	)
}

export default LatestPosts
